/**
 * Real Backend API Types
 * These match the actual API response structure from your backend
 */

// Real User (Customer) from backend
export interface RealUser {
  id: number;
  name: string;
  username: string;
  bio: string;
  countryCode: string;
  mobileNumber: string;
  email: string;
  status: string;
  image: string | null;
  uid: string | null;
  role: string;
  latitude: number | null;
  longitude: number | null;
  location: string | null;
  createdAt: string;
  isOnline: boolean | null;
  signInProvider?: string[] | null;
}

export interface RealUsersResponse {
  count: number;
  results: RealUser[];
}

// Real Provider from backend
export interface RealProvider {
  id: number;
  name: string;
  username: string;
  bio: string;
  countryCode: string;
  mobileNumber: string;
  email: string;
  status: 'active' | 'inactive';
  image: string | null;
  uid: string | null;
  role: 'provider';
  latitude: number | null;
  longitude: number | null;
  location: string | null;
  createdAt: string;
  providerRatings: any[];
  isFeatured: boolean;
  totalServices?: number; // Total number of services
  averageRating?: number; // Average rating calculated from providerRatings
  signInProvider?: string[] | null;
}

export interface RealProvidersResponse {
  count: number;
  deletedCount?: number; // Include deleted count if available
  results: RealProvider[];
}

// Adapter to convert real user API response to internal customer format
export function adaptRealUser(realUser: RealUser) {
  // Handle phone number logic
  let phone = 'N/A';

  if (realUser.mobileNumber && realUser.mobileNumber.trim() !== '') {
    const countryCode =
      realUser.countryCode && realUser.countryCode.trim() !== '' ? realUser.countryCode : '1';
    // Remove existing + if present
    const cleanCode = countryCode.startsWith('+') ? countryCode.substring(1) : countryCode;
    phone = `+${cleanCode}-${realUser.mobileNumber}`;
  }

  return {
    id: String(realUser.id),
    name: realUser.name,
    email: realUser.email,
    phone: phone,
    status: realUser.status,
    joinedAt: realUser.createdAt,
    username: realUser.username,
    bio: realUser.bio,
    image: realUser.image,
    signInProvider: realUser.signInProvider || [],
    location: realUser.location,
    isOnline: realUser.isOnline,
  };
}

// Adapter to convert real API response to internal format
export function adaptRealProvider(realProvider: RealProvider) {
  const countryCode =
    realProvider.countryCode && realProvider.countryCode.trim() !== ''
      ? realProvider.countryCode
      : '1';
  // Remove existing + if present
  const cleanCode = countryCode.startsWith('+') ? countryCode.substring(1) : countryCode;

  // Calculate average rating from providerRatings or use backend-provided averageRating
  let averageRating = 0;

  // Use backend-provided averageRating if available
  if (realProvider.averageRating && typeof realProvider.averageRating === 'number') {
    averageRating = realProvider.averageRating;
  } else if (realProvider.providerRatings && realProvider.providerRatings.length > 0) {
    // Fallback: calculate from providerRatings array
    const validRatings = realProvider.providerRatings.filter(
      (rating) => rating && typeof rating.rating === 'number'
    );
    if (validRatings.length > 0) {
      const sum = validRatings.reduce((acc, rating) => acc + rating.rating, 0);
      averageRating = Math.round((sum / validRatings.length) * 10) / 10; // Round to 1 decimal place
    }
  }

  return {
    id: String(realProvider.id),
    name: realProvider.name,
    email: realProvider.email,
    phone: `+${cleanCode}-${realProvider.mobileNumber}`,
    rating: 0, // Default rating - actual rating will come from provider details endpoint
    verified: realProvider.status === 'active', // Assume active = verified
    status: realProvider.status,
    reviews: [], // Remove mock reviews - will be populated from provider details endpoint
    services: [], // Remove mock services - will be populated from provider details endpoint
    username: realProvider.username,
    bio: realProvider.bio,
    image: realProvider.image,
    signInProvider: realProvider.signInProvider || [],
    location: realProvider.location,
    createdAt: realProvider.createdAt,
    isFeatured: realProvider.isFeatured,
    totalServices: realProvider.totalServices || 0,
    averageRating,
  };
}
